import * as Sentry from "@sentry/react";
import {
  FunctionsFetchError,
  FunctionsHttpError,
  FunctionsRelayError,
} from "@supabase/supabase-js";
import { supabase } from "../lib/supabase";
import type {
  ExportDownload,
  ExportFilterLabels,
  ExportFilters,
  ExportHistoryCursor,
  ExportHistoryPage,
  ExportJob,
  ExportJobStatus,
  ExportOption,
  ExportOptionKind,
  ExportPreview,
} from "../types/exports";

const OPTION_PAGE_SIZE = 250;
const HISTORY_PAGE_SIZE = 25;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const DATE = /^\d{4}-\d{2}-\d{2}$/;
const HISTORY_COLUMNS =
  "id,created_at,requested_by_name,date_from,date_to_exclusive,filter_labels,worker_scope_mode,status,row_count,processed_rows,warning_row_count,snapshot_at,finished_at,error_message,file_available,expires_at";

const NOT_INSTALLED =
  "Die Exportfunktion ist auf dieser Datenbank nicht installiert.";
const SESSION_EXPIRED = "Sitzung abgelaufen. Bitte neu anmelden.";
// Codes with a cause the admin can act on; everything else keeps the
// operation's generic message. Never surface server messages or details.
const ERROR_MESSAGES: Readonly<Record<string, string>> = {
  "42501": "Kein Exportzugriff. Bitte Admin-Zuordnung prüfen.",
  "42883": NOT_INSTALLED,
  PGRST202: NOT_INSTALLED,
  PGRST301: SESSION_EXPIRED,
  PGRST303: SESSION_EXPIRED,
};

function serviceError(
  operation: string,
  error: unknown,
  fallback: string
): Error {
  const code =
    error && typeof error === "object" && "code" in error
      ? String((error as { code: unknown }).code)
      : undefined;
  const known = code === undefined ? undefined : ERROR_MESSAGES[code];
  if (!known)
    Sentry.addBreadcrumb({
      category: "exports",
      level: "warning",
      message: `${operation} failed`,
      data: { code: code ?? "unknown" },
    });
  return new Error(known ?? fallback);
}

const UNREACHABLE =
  "Der Exportdienst ist nicht erreichbar. Bitte später erneut versuchen.";

function functionStatus(error: unknown): number | null {
  return error instanceof FunctionsHttpError &&
    error.context instanceof Response
    ? error.context.status
    : null;
}

function functionError(error: unknown, fallback: string): Error {
  if (
    error instanceof FunctionsFetchError ||
    error instanceof FunctionsRelayError
  )
    return new Error(UNREACHABLE);
  const status = functionStatus(error);
  if (status === 401) return new Error(SESSION_EXPIRED);
  return serviceError(
    "download-export",
    { code: status ?? "unknown" },
    fallback
  );
}

function invalidResponse(): never {
  throw new Error(
    "Die Exportdaten konnten nicht gelesen werden. Bitte erneut versuchen."
  );
}

function record(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value))
    invalidResponse();
  return value as Record<string, unknown>;
}

function string(value: unknown): string {
  if (typeof value !== "string") invalidResponse();
  return value;
}

function number(value: unknown): number {
  if (typeof value !== "number" || !Number.isSafeInteger(value) || value < 0)
    invalidResponse();
  return value;
}

function optionalString(value: unknown): string | null {
  return value === null ? null : string(value);
}

function boolean(value: unknown): boolean {
  if (typeof value !== "boolean") invalidResponse();
  return value;
}

function catalogActive(value: unknown): boolean {
  if (value !== null && typeof value !== "boolean") invalidResponse();
  // The historical catalog schema permits NULL for several active columns.
  return value === true;
}

function array(value: unknown): unknown[] {
  if (!Array.isArray(value)) invalidResponse();
  return value;
}

function labels(value: unknown): ExportFilterLabels {
  const source = record(value);
  const parse = (key: keyof ExportFilterLabels) =>
    array(source[key]).map((item) => {
      const label = record(item);
      const id = number(label["id"]);
      return {
        id,
        label:
          label["label"] === null ? `Eintrag ${id}` : string(label["label"]),
      };
    });
  return {
    providers: parse("providers"),
    contract_types: parse("contract_types"),
    teams: parse("teams"),
    workers: parse("workers"),
  };
}

function parseOption(value: unknown): ExportOption {
  const item = record(value);
  const id = number(item["id"]);
  const option: ExportOption = {
    id,
    label: item["label"] === null ? `Mitarbeiter ${id}` : string(item["label"]),
    active: catalogActive(item["active"]),
  };
  if (item["provider_id"] !== undefined)
    option.provider_id = number(item["provider_id"]);
  if (item["provider"] !== undefined)
    option.provider = string(item["provider"]);
  return option;
}

function checkFilters(filters: ExportFilters): void {
  if (
    !DATE.test(filters.date_from) ||
    !DATE.test(filters.date_to_exclusive) ||
    filters.date_from >= filters.date_to_exclusive ||
    !Array.isArray(filters.provider_ids) ||
    filters.provider_ids.length === 0 ||
    ![
      filters.provider_ids,
      filters.contract_type_ids,
      filters.team_ids,
      filters.worker_ids,
    ].every(
      (ids) =>
        Array.isArray(ids) &&
        ids.every((id) => Number.isSafeInteger(id) && id > 0)
    )
  ) {
    throw new Error(
      "Bitte einen gültigen Zeitraum und mindestens einen Anbieter auswählen."
    );
  }
}

/** Load every bounded ID page so search covers the complete option set. */
export async function loadExportOptions(
  kind: ExportOptionKind,
  providerIds: number[] = []
): Promise<ExportOption[]> {
  if (
    !Array.isArray(providerIds) ||
    !providerIds.every((id) => Number.isSafeInteger(id) && id > 0)
  )
    throw new Error("Ungültige Anbieter-Auswahl.");
  const items: ExportOption[] = [];
  let cursor = 0;
  for (;;) {
    const { data, error } = await supabase.rpc("get_export_options", {
      p_kind: kind,
      p_filters: { provider_ids: providerIds },
      p_after_id: cursor,
      p_limit: OPTION_PAGE_SIZE,
    });
    if (error)
      throw serviceError(
        "get_export_options",
        error,
        "Auswahlmöglichkeiten konnten nicht geladen werden."
      );
    const page = record(data);
    const batch = array(page["items"]).map(parseOption);
    const hasMore = boolean(page["has_more"]);
    const nextCursor =
      page["next_cursor"] === null ? null : number(page["next_cursor"]);
    if (
      batch.some(
        (item, index) =>
          item.id <= (index === 0 ? cursor : batch[index - 1]!.id)
      ) ||
      (batch.length > 0 && nextCursor !== batch[batch.length - 1]!.id) ||
      (hasMore && (batch.length === 0 || nextCursor === null))
    )
      invalidResponse();
    items.push(...batch);
    if (!hasMore) return items;
    cursor = nextCursor!;
  }
}

/** One bounded suggestion page; callers may follow next_cursor for Add All. */
export async function previewExport(
  filters: ExportFilters,
  afterWorkerId = 0
): Promise<ExportPreview> {
  checkFilters(filters);
  if (!Number.isSafeInteger(afterWorkerId) || afterWorkerId < 0)
    invalidResponse();
  const { data, error } = await supabase.rpc("preview_contract_export", {
    p_filters: filters,
    p_after_worker_id: afterWorkerId,
    p_limit: OPTION_PAGE_SIZE,
  });
  if (error)
    throw serviceError(
      "preview_contract_export",
      error,
      "Die Vorschau konnte nicht geladen werden."
    );
  const page = record(data);
  const missingWorkers = array(page["missing_workers"]).map((value) => {
    const worker = record(value);
    const id = number(worker["id"]);
    return {
      id,
      name:
        worker["name"] === null ? `Mitarbeiter ${id}` : string(worker["name"]),
      contract_count: number(worker["contract_count"]),
    };
  });
  const hasMore = boolean(page["has_more"]);
  const nextCursor =
    page["next_cursor"] === null ? null : number(page["next_cursor"]);
  if (
    missingWorkers.some(
      (worker, index) =>
        worker.id <=
        (index === 0 ? afterWorkerId : missingWorkers[index - 1]!.id)
    ) ||
    (missingWorkers.length > 0 &&
      nextCursor !== missingWorkers[missingWorkers.length - 1]!.id) ||
    (hasMore && (missingWorkers.length === 0 || nextCursor === null))
  )
    invalidResponse();
  return {
    matching_contracts: number(page["matching_contracts"]),
    missing_contracts: number(page["missing_contracts"]),
    missing_worker_count: number(page["missing_worker_count"]),
    missing_workers: missingWorkers,
    has_more: hasMore,
    next_cursor: nextCursor,
  };
}

/** Collect the complete suggestion set before the caller changes its worker selection. */
export async function loadAllMissingWorkerIds(
  filters: ExportFilters,
  expectedCount: number,
  isCurrent: () => boolean
): Promise<number[]> {
  const ids = new Set<number>();
  let cursor = 0;
  for (let page = 0; page < 1000; page += 1) {
    if (!isCurrent()) throw new Error("Die Filterauswahl hat sich geändert.");
    const result = await previewExport(filters, cursor);
    if (!isCurrent()) throw new Error("Die Filterauswahl hat sich geändert.");
    result.missing_workers.forEach((worker) => ids.add(worker.id));
    if (!result.has_more) {
      if (ids.size !== expectedCount)
        throw new Error(
          "Die Verkaufsdaten haben sich geändert. Bitte Vorschau aktualisieren und erneut versuchen."
        );
      return [...ids];
    }
    if (result.next_cursor === null || result.next_cursor <= cursor) break;
    cursor = result.next_cursor;
  }
  throw new Error(
    "Die Mitarbeiterliste konnte nicht vollständig geladen werden."
  );
}

export async function createExport(
  filters: ExportFilters,
  key: string
): Promise<string> {
  checkFilters(filters);
  if (!UUID.test(key)) throw new Error("Ungültige Export-Anfrage.");
  const { data, error } = await supabase.rpc("create_contract_export", {
    p_filters: filters,
    p_idempotency_key: key,
  });
  if (error)
    throw serviceError(
      "create_contract_export",
      error,
      "Der Export konnte nicht angelegt werden. Bitte Auswahl prüfen."
    );
  if (typeof data !== "string" || !UUID.test(data)) invalidResponse();
  return data;
}

export async function retryExport(jobId: string, key: string): Promise<string> {
  if (!UUID.test(jobId) || !UUID.test(key))
    throw new Error("Ungültige Export-Anfrage.");
  const { data, error } = await supabase.rpc("retry_contract_export", {
    p_failed_job_id: jobId,
    p_idempotency_key: key,
  });
  if (error)
    throw serviceError(
      "retry_contract_export",
      error,
      "Der Export konnte nicht erneut gestartet werden."
    );
  if (typeof data !== "string" || !UUID.test(data)) invalidResponse();
  return data;
}

function parseJob(value: unknown): ExportJob {
  const row = record(value);
  const status = string(row["status"]) as ExportJobStatus;
  if (
    !(
      [
        "pending",
        "running",
        "finished_successfully",
        "finished_with_errors",
        "error",
      ] as string[]
    ).includes(status)
  )
    invalidResponse();
  const workerScope = string(row["worker_scope_mode"]);
  if (workerScope !== "all" && workerScope !== "selected") invalidResponse();
  const id = string(row["id"]);
  if (!UUID.test(id)) invalidResponse();
  return {
    id,
    created_at: string(row["created_at"]),
    requested_by_name: optionalString(row["requested_by_name"]),
    date_from: string(row["date_from"]),
    date_to_exclusive: string(row["date_to_exclusive"]),
    filter_labels: labels(row["filter_labels"]),
    worker_scope_mode: workerScope,
    status,
    row_count: row["row_count"] === null ? null : number(row["row_count"]),
    processed_rows: number(row["processed_rows"]),
    warning_row_count: number(row["warning_row_count"]),
    snapshot_at: optionalString(row["snapshot_at"]),
    finished_at: optionalString(row["finished_at"]),
    error_message: optionalString(row["error_message"]),
    file_available: boolean(row["file_available"]),
    expires_at: optionalString(row["expires_at"]),
  };
}

/** RLS limits rows to the current admin's organization; keyset keeps history stable. */
export async function listExportJobs(
  cursor: ExportHistoryCursor | null = null
): Promise<ExportHistoryPage> {
  let query = supabase
    .from("export_jobs")
    .select(HISTORY_COLUMNS)
    .order("created_at", { ascending: false })
    .order("id", { ascending: false })
    .limit(HISTORY_PAGE_SIZE + 1);
  if (cursor) {
    if (
      !UUID.test(cursor.id) ||
      !/^\d{4}-\d{2}-\d{2}T[\d:.+-]+(?:Z|[+-]\d{2}:\d{2})?$/.test(
        cursor.created_at
      )
    )
      throw new Error("Ungültige Export-Seite.");
    query = query.or(
      `created_at.lt.${cursor.created_at},and(created_at.eq.${cursor.created_at},id.lt.${cursor.id})`
    );
  }
  const { data, error } = await query;
  if (error)
    throw serviceError(
      "export_jobs",
      error,
      "Die Exportliste konnte nicht geladen werden."
    );
  const rows = array(data).map(parseJob);
  const hasMore = rows.length > HISTORY_PAGE_SIZE;
  const jobs = rows.slice(0, HISTORY_PAGE_SIZE);
  const last = jobs[jobs.length - 1];
  return {
    jobs,
    nextCursor:
      hasMore && last ? { created_at: last.created_at, id: last.id } : null,
    hasMore,
  };
}

/** Ask the authorized Edge endpoint for a new 60-second signed URL on every click. */
export async function getExportDownload(
  jobId: string
): Promise<ExportDownload> {
  if (!UUID.test(jobId)) throw new Error("Ungültiger Export.");
  const { data, error } = await supabase.functions.invoke("download-export", {
    body: { job_id: jobId },
  });
  if (error)
    throw functionError(error, "Die Exportdatei ist derzeit nicht verfügbar.");
  const result = record(data);
  const url = string(result["url"]);
  const filename = string(result["filename"]);
  const parsed = new URL(url);
  if (
    !(["http:", "https:"] as string[]).includes(parsed.protocol) ||
    !filename.endsWith(".csv")
  )
    invalidResponse();
  return { url, filename, expires_in: number(result["expires_in"]) };
}

function inclusiveEnd(dateToExclusive: string): string {
  const end = new Date(`${dateToExclusive}T00:00:00Z`);
  end.setUTCDate(end.getUTCDate() - 1);
  return end.toISOString().slice(0, 10);
}

/**
 * The separate "Datenhinweise" report: one row per contract with data warnings.
 * Built server-side from the caller's own authorized reads on every click.
 */
export async function getExportWarningsFile(
  job: Pick<ExportJob, "id" | "date_from" | "date_to_exclusive">
): Promise<{ blob: Blob; filename: string }> {
  if (!UUID.test(job.id)) throw new Error("Ungültiger Export.");
  const { data, error } = await supabase.functions.invoke("download-export", {
    body: { job_id: job.id, kind: "warnings" },
  });
  if (error) {
    if (functionStatus(error) === 404)
      throw new Error(
        "Für diesen älteren Export sind keine Datenhinweise gespeichert."
      );
    throw functionError(
      error,
      "Die Datenhinweise sind derzeit nicht verfügbar."
    );
  }
  if (!(data instanceof Blob)) invalidResponse();
  if (!DATE.test(job.date_from) || !DATE.test(job.date_to_exclusive))
    invalidResponse();
  return {
    blob: new Blob([data], { type: "text/csv;charset=utf-8" }),
    filename: `Datenhinweise_${job.date_from}_${inclusiveEnd(
      job.date_to_exclusive
    )}_${job.id.slice(0, 8)}.csv`,
  };
}
